import TR from 'utils/TR';
import T from 'utils/T';
import Recoil from 'recoil';
import React from 'react';
import * as Luxon from 'luxon'

import ex from 'utils/ex';
import PageContent from './PageContent';
import HBox from 'components/common/HBox';
import LinearCalendar from 'components/common/LinearCalendar';
import useBldsugrExistDate from 'hooks/communication/useBldsugrExistDate';
import * as AppStore from 'stores/AppStore';
import useForceUpdate from 'hooks/common/useForceUpdate';
import { fromFormat, luxonClone } from 'utils/time';
import VBox from 'components/common/VBox';
import AdviceChart from 'components/charts/AdviceChart';
import useInsulinRecommendInjection from 'hooks/communication/useInsulinRecommendInjection';
import useDetectEffect from 'hooks/common/useDetectEffect';


const onDisable = () => false;
export default function Advice(props) {
  const {
    renderAll,
    front,
    ...rest
  } = props || {};

  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);

  const {
    requestBldsugrExistDate,
  } = useBldsugrExistDate();
  const [glocose_exists, setGlocoseExists] = Recoil.useRecoilState(AppStore.assets.glocose_exists2);
  // React.useEffect(() => {
  //   setGlocoseExists(() => ({}));
  // }, [setGlocoseExists]);

  const onPeriodChanged = React.useCallback((start, end) => {
    requestBldsugrExistDate({
      search_start_time: start,
      search_end_time: end,
      patient_no,
      search_type: 'M',
    }, (success, data) => {
      if (success) {
        setGlocoseExists(() => ({ ...(data.bldsugr_date_list.reduce((a, c) => ({ ...a, [c]: true }), {})) }));
      } else {
        setGlocoseExists(() => ({}));
      }
    });
  }, [patient_no, requestBldsugrExistDate, setGlocoseExists]);

  React.useEffect(() => {
    onPeriodChanged(period_start, period_end);
  }, [onPeriodChanged, period_end, period_start]);

  const [selection, setSelection] = Recoil.useRecoilState(AppStore.assets.selected_period);
  const select = React.useCallback((date, end) => {
    setSelection(() => date);
  }, [setSelection]);


  const luxon_start = React.useMemo(() => Luxon.DateTime.fromFormat(period_start, 'yyyyMMdd'), [period_start]);
  const luxon_end = React.useMemo(() => Luxon.DateTime.fromFormat(period_end, 'yyyyMMdd'), [period_end]);

  const timestamp = selection ? selection.valueOf() : 0;
  const exists = Object.keys(glocose_exists).length !== 0;
  React.useEffect(() => {
    if (selection !== null && exists && luxon_start <= selection && selection <= luxon_end) return;
    select(luxon_start);
    // onPeriodChanged(period_start, period_end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamp, onPeriodChanged, patient_no, period_end, period_start, select, luxon_start, luxon_end, exists]);

  const onSelect = React.useCallback((date, end) => (e) => {
    select(date, end);
  }, [select]);

  const month_start = React.useMemo(() => Luxon.DateTime.fromFormat(period_start, 'yyyyMMdd').startOf('month'), [period_start]);
  const month_end = React.useMemo(() => luxonClone(luxon_end).endOf('month'), [luxon_end]);

  const { weeks } = month_end.diff(month_start, ['weeks']);

  const onWheel = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const {
    requestInsulinRecommendInjection,
  } = useInsulinRecommendInjection();
  const [data, setData] = React.useState({});

  React.useEffect(() => {
    if (!selection) return;
    const search_time = selection.toFormat('yyyyMMdd');
    requestInsulinRecommendInjection({
      search_time,
      patient_no,
    }, (success, data) => {
      if (success) {
        setData(() => data);
      }
    });
  }, [patient_no, requestInsulinRecommendInjection, selection]);

  const {
    tot_bolus_injection_amt = 0,
    tot_bolus_injection_cnt = 0,
    bldsugr_insulin_list = [],
    default_bldsugr,
    default_bldsugr_time,
  } = data || {};

  const date = selection ? selection.toFormat('yyyy-MM-dd') : '';

  return (
    <div
      {...ex(['Page', 'Advice'], rest)}
      {...{ onWheelCapture: onWheel }}
    >
      <PageContent {...rest}>
        <HBox full>
          <LinearCalendar
            key={period_start + period_end}
            {...{
              date_availables: glocose_exists,
              selection_start: null,
              selection_end: null,
              selection,
              onSelect,
              min: luxon_start,
              max: luxon_end,
              length: Math.ceil(weeks + 3 + 3),
              basis: month_end,
              onPeriodChanged: onPeriodChanged,
              onDisable,
            }}
          />
          <VBox full>
            <AdviceChart {...{ chartData: data }} />
            <VBox full>
              <HBox {...ex('Caption')}>
                <div>
                  {date}
                </div>
                <div>
                  {T('볼루스 총 {0} U, 볼루스 주입 {1} 회', tot_bolus_injection_amt, tot_bolus_injection_cnt)}
                </div>
              </HBox>
              <VBox full {...ex('Scroll')}>
                <div>
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <div>
                            {T('혈당시간')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('혈당')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('예측혈당')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('예측혈당시간')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('이벤트')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('인슐린종류')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('인슐린 권장 투여량')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('실제 인슐린 투여량')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('인슐린 구분')}
                          </div>
                        </th>
                        <th>
                          <div>
                            {T('인슐린 투여 시간')}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><div>{default_bldsugr_time}</div></td>
                        {/* <td><div>{sum_bldsugr_time.join('\n')}</div></td> */}
                        <td><div>{default_bldsugr}</div></td>
                        <td><div></div></td>
                        <td><div></div></td>
                        <td><div></div></td>
                        {/* <td><div>{sum_insulin_type.join('\n')}</div></td> */}
                        <td><div>{ }</div></td>
                        <td><div>{ }</div></td>
                        <td><div></div></td>
                        {/* <td><div>{sum_insulin_tp_cd.join('\n')}</div></td> */}
                        <td><div></div></td>
                        <td><div></div></td>
                      </tr>
                      {bldsugr_insulin_list.map((info, index) => {
                        const {
                          time,
                          bldsugr_list = [],
                          recommend_insulin_list = [],
                          real_insulin_list = [],
                        } = info;

                        const sum_bldsugr_time = [];
                        const sum_bldsugr = [];
                        const sum_predict_bldsugr = [];
                        const sum_predict_bldsugr_time = [];
                        const sum_event_name = [];
                        const sum_insulin_type = [];
                        const sum_insulin_type_name = [];
                        const sum_insulin_recommend_amt = [];
                        const sum_insulin_real_amt = [];
                        const sum_insulin_tp_cd = [];
                        const sum_insulin_tp_cd_nm = [];
                        const sum_insulin_injected_time = [];

                        bldsugr_list.forEach(item => {
                          const {
                            bldsugr_time,
                            bldsugr,
                            predict_bldsugr,
                            predict_bldsugr_time,
                          } = item;
                          sum_bldsugr_time.push(fromFormat(bldsugr_time, 'HHmmss').toFormat('HH:mm:ss'));
                          sum_bldsugr.push(bldsugr);
                          sum_predict_bldsugr.push(predict_bldsugr);
                          sum_predict_bldsugr_time.push(fromFormat(predict_bldsugr_time, 'HHmmss').toFormat('HH:mm:ss'));
                        });
                        recommend_insulin_list.forEach(item => {
                          const {
                            event_name,
                            insulin_type,
                            insulin_type_name,
                            insulin_recommend_amt,
                          } = item;
                          sum_event_name.push(event_name);
                          sum_insulin_type.push(insulin_type);
                          sum_insulin_type_name.push(insulin_type_name);
                          sum_insulin_recommend_amt.push(insulin_recommend_amt);
                        });
                        real_insulin_list.forEach(item => {
                          const {
                            insulin_real_amt,
                            insulin_tp_cd,
                            insulin_tp_cd_nm,
                            insulin_injected_time,
                          } = item;

                          sum_insulin_real_amt.push(insulin_real_amt);
                          sum_insulin_tp_cd.push(insulin_tp_cd);
                          sum_insulin_tp_cd_nm.push(insulin_tp_cd_nm);
                          sum_insulin_injected_time.push(fromFormat(insulin_injected_time, 'HHmmss').toFormat('HH:mm:ss'));
                        });
                        return (
                          <tr key={index}>
                            <td><div>{sum_bldsugr_time.join('\n')}</div></td>
                            {/* <td><div>{sum_bldsugr_time.join('\n')}</div></td> */}
                            <td><div>{sum_bldsugr.join('\n')}</div></td>
                            <td><div>{sum_predict_bldsugr.join('\n')}</div></td>
                            <td><div>{sum_predict_bldsugr_time.join('\n')}</div></td>
                            <td><div>{sum_event_name.join('\n')}</div></td>
                            {/* <td><div>{sum_insulin_type.join('\n')}</div></td> */}
                            <td><div>{sum_insulin_type_name.join('\n')}</div></td>
                            <td><div>{sum_insulin_recommend_amt.join('\n')}</div></td>
                            <td><div>{sum_insulin_real_amt.join('\n')}</div></td>
                            {/* <td><div>{sum_insulin_tp_cd.join('\n')}</div></td> */}
                            <td><div>{sum_insulin_tp_cd_nm.join('\n')}</div></td>
                            <td><div>{sum_insulin_injected_time.join('\n')}</div></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </VBox>
            </VBox>
          </VBox>
        </HBox>
      </PageContent>
    </div>
  );
}